import { CREDENTIAL_FORM_DESCRIPTORS } from "@api/shared-runtime/credential-form-descriptors";
import { CredentialType } from "@api/shared-runtime/enums";
import {
  buildSubstance,
  formatStringList,
  initialFormValues,
  parseStringList,
  type CredentialFormValues,
} from "@/features/credentials/components/credential-edit-modal/credential-form-values";
import type { Credential } from "@/features/credentials/types";
import {
  PERSONAL_INFO_FIELD_GROUPS,
  personalInfoFieldKey,
} from "./personal-info-fields";

const PROMOTED_COLUMNS = [
  "number",
  "stateCode",
  "issuedAt",
  "expiresAt",
  "status",
] as const;

function record(value: unknown): Record<string, unknown> {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

export function initialProviderFormValues(
  credential: Credential,
): CredentialFormValues {
  const descriptor =
    CREDENTIAL_FORM_DESCRIPTORS[credential.credentialType as CredentialType];
  const values = initialFormValues(descriptor, credential);
  if (credential.credentialType !== CredentialType.PersonalInfo) {
    return values;
  }

  for (const group of PERSONAL_INFO_FIELD_GROUPS) {
    const existing = record(record(credential.version?.details)[group.key]);
    for (const field of group.fields) {
      const key = personalInfoFieldKey(group.key, field.key);
      const value = existing[field.key];
      if (field.kind === "string-list") {
        values[key] = formatStringList(value);
      } else {
        values[key] = typeof value === "string" ? value : "";
      }
    }
  }
  return values;
}

function mergePersonalInfo({
  details,
  values,
  baseline,
}: {
  details: Record<string, unknown>;
  values: CredentialFormValues;
  baseline: CredentialFormValues;
}) {
  for (const group of PERSONAL_INFO_FIELD_GROUPS) {
    const nested = { ...record(details[group.key]) };
    let changed = false;
    for (const field of group.fields) {
      const key = personalInfoFieldKey(group.key, field.key);
      if (values[key] === baseline[key]) {
        continue;
      }
      changed = true;
      const value = String(values[key] ?? "").trim();
      delete nested[field.key];
      if (field.key === "specialtyCodes") {
        // An explicit empty selection overrides specialties inferred from NPI.
        nested[field.key] = parseStringList(value);
        continue;
      }
      if (value === "") {
        continue;
      }
      nested[field.key] =
        field.kind === "string-list" ? parseStringList(value) : value;
    }
    // Copy untouched nested records exactly; editing one address field must
    // retain its unrendered postal, phone, and fax values.
    if (!changed) {
      continue;
    }
    if (Object.keys(nested).length === 0) {
      delete details[group.key];
    } else {
      details[group.key] = nested;
    }
  }
}

export function buildProviderCredentialSubstance({
  credential,
  values,
  baselineValues,
}: {
  credential: Credential;
  values: CredentialFormValues;
  baselineValues?: CredentialFormValues;
}) {
  const descriptor =
    CREDENTIAL_FORM_DESCRIPTORS[credential.credentialType as CredentialType];
  const baseline = initialProviderFormValues(credential);
  const currentDetails = record(credential.version?.details);
  const substance = buildSubstance(descriptor, values, currentDetails);

  // A version replaces the full substance, so columns absent from this type's
  // form and explicit false values must survive an unrelated correction.
  for (const column of PROMOTED_COLUMNS) {
    if (
      !descriptor.fields.some(
        (field) => field.source === "column" && field.key === column,
      )
    ) {
      substance[column] = credential.version?.[column] ?? undefined;
    }
  }
  for (const field of descriptor.fields) {
    if (field.kind !== "boolean") {
      continue;
    }
    const key = `${field.source}.${field.key}`;
    if (field.required) {
      substance.details[field.key] = values[key] === true;
      continue;
    }
    if (values[key] === baseline[key] && currentDetails[field.key] === false) {
      substance.details[field.key] = false;
    }
    if (baseline[key] === true && values[key] === false) {
      substance.details[field.key] = false;
    }
  }
  if (credential.credentialType === CredentialType.PersonalInfo) {
    mergePersonalInfo({
      details: substance.details,
      values,
      baseline: baselineValues ?? baseline,
    });
  }
  return substance;
}

export function validateProviderForm({
  credential,
  values,
}: {
  credential: Credential;
  values: CredentialFormValues;
}): string | null {
  const descriptor =
    CREDENTIAL_FORM_DESCRIPTORS[credential.credentialType as CredentialType];
  for (const field of descriptor.fields) {
    const value = values[`${field.source}.${field.key}`];
    if (field.kind === "boolean") {
      continue;
    }
    const text = typeof value === "string" ? value.trim() : "";
    if (field.required && !text && value !== true) {
      return `${field.label} is required.`;
    }
    if (field.kind === "number" && text && !Number.isFinite(Number(text))) {
      return `Enter a valid number for ${field.label.toLowerCase()}.`;
    }
  }
  if (credential.credentialType === CredentialType.PersonalInfo) {
    const nameFields = PERSONAL_INFO_FIELD_GROUPS[0].fields;
    const hasName = nameFields.some((field) =>
      String(values[personalInfoFieldKey("name", field.key)] ?? "").trim(),
    );
    if (
      hasName &&
      (!String(values[personalInfoFieldKey("name", "first")] ?? "").trim() ||
        !String(values[personalInfoFieldKey("name", "last")] ?? "").trim())
    ) {
      return "Enter both a first and last name.";
    }
  }
  return null;
}
